import {
  parseReleaseIndex,
  productFor,
  updateToOffer,
  variantForDevice,
  type AvailableRelease,
} from '@player/domain';
import { detectPlatform } from '@player/focus-keyboard';
import { appVersion } from './version.js';
import { releaseBaseUrl } from './releaseConfig.js';

/**
 * «C'è una versione nuova?», chiesto una volta per avvio.
 *
 * ## Perché una volta e non a orologio
 *
 * Perché un rilascio è un evento che capita qualche volta all'anno, e un televisore resta accesso
 * per ore. Un controllo periodico costerebbe una richiesta ogni tanto a ogni apparecchio installato
 * per una notizia che, quando arriva, può aspettare il riavvio successivo — e l'app si riavvia ogni
 * volta che qualcuno la apre.
 *
 * È la stessa regola che vale per il catalogo: si chiede quando serve, non a cadenza.
 *
 * ## Si ferma al primo errore
 *
 * Nessun ritentativo. Un registro che risponde male o non risponde si legge come «non lo so», che è
 * l'unica cosa onesta, e si riprova al prossimo avvio. Insistere su un apparecchio in un'azienda
 * dove quell'host è bloccato vorrebbe dire una raffica di richieste a ogni avvio, per sempre.
 *
 * ## Quando non risponde niente, e non è un guasto
 *
 * Tre casi, tutti normali:
 *
 * - **da un browser**: non c'è niente di installato da aggiornare, la pagina si ricarica ed è già
 *   l'ultima versione (`productFor` risponde `null`);
 * - **dal server di sviluppo o dalle prove**: l'app non sa di che versione è, e un confronto con
 *   `null` proporrebbe un aggiornamento a caso (`appVersion` risponde `null`);
 * - **quando si è già all'ultima**: `updateToOffer` risponde `null`, che è il caso di gran lunga più
 *   frequente.
 *
 * Nessuno dei tre va distinto dall'altro da chi chiama: la domanda è «cosa propongo», e la risposta
 * è «niente».
 */
export async function checkForUpdate(
  options: {
    readonly fetchImpl?: typeof fetch;
    readonly userAgent?: string;
    readonly signal?: AbortSignal;
  } = {},
): Promise<AvailableRelease | null> {
  const current = appVersion();
  if (current === null) return null;

  const product = productFor(detectPlatform(options.userAgent ?? navigator.userAgent));
  if (product === null) return null;

  // `…/v1/apps/player-android/index.json` sull'autenticatore: lo stesso host dell'accoppiamento,
  // perché è l'unico che l'app conosce prima di avere un operatore. Vedi `releaseConfig.ts`.
  const url = `${releaseBaseUrl()}/${product}/index.json`;
  const get = options.fetchImpl ?? fetch;

  let payload: unknown;
  try {
    const response = await get(url, { signal: options.signal });
    if (!response.ok) return null;
    payload = await response.json();
  } catch {
    // Rete assente, host bloccato, risposta che non è JSON: tutti «non lo so». L'aggiornamento è
    // una comodità, non una condizione per guardare la televisione, e un errore qui non deve
    // comparire da nessuna parte.
    return null;
  }

  const releases = parseReleaseIndex(payload);
  if (releases === null) return null;

  const offerta = updateToOffer(releases, current);
  if (offerta === null) return null;

  /*
    Fra i file di quella versione, quello per questa architettura.

    L'involucro Android lo dice su `/device.json`; altrove l'elenco è vuoto e si prende l'universale,
    che è l'unico file che Tizen e webOS pubblichino comunque — là il pacchetto non porta codice
    nativo, quindi una variante sola è tutto quel che esiste.
  */
  const scelta = variantForDevice(await deviceAbis(get, options.signal), offerta.variants);
  if (scelta === null) {
    // Nessun file installabile su questo apparecchio: meglio nessun avviso che un avviso che porta a
    // «app non installata» dopo centonovanta megabyte.
    return null;
  }

  // La versione resta quella della release; cambiano i byte da scaricare. Chi mostra l'avviso parla
  // di una versione, chi scarica usa `url`, `sha256` e `size` — che adesso sono quelli del file
  // giusto e non quelli dell'universale.
  return { ...offerta, size: scelta.chosen.size, sha256: scelta.chosen.sha256, url: scelta.chosen.url };
}

/**
 * Cosa sa eseguire questo apparecchio, chiesto all'involucro nativo.
 *
 * ## Perché una richiesta e non una variabile
 *
 * Perché l'applicazione web **non può vedere** l'architettura: il `User-Agent` del motore dice il
 * sistema, non la macchina. L'involucro Android la serve su `/device.json`, sulla stessa origine che
 * serve l'app — quindi è una `fetch` normale, senza CORS e senza nessun ponte JavaScript da
 * difendere.
 *
 * ## Elenco vuoto dove quell'involucro non c'è, e non è un ripiego mascherato
 *
 * Su Tizen, webOS e nel browser `/device.json` non esiste e qui si risponde `[]`. Il risultato è che
 * si prende l'universale, **che su quelle piattaforme è l'unico file pubblicato**: i loro pacchetti
 * non portano codice nativo, quindi non c'è nessuna scelta da sbagliare. La stessa risposta vale per
 * un Android in cui l'involucro non rispondesse: lì l'universale si installa comunque.
 */
async function deviceAbis(
  get: typeof fetch,
  signal: AbortSignal | undefined,
): Promise<ReadonlyArray<string>> {
  try {
    // Indirizzo relativo: l'app è servita dall'involucro, quindi è casa propria. Scriverlo assoluto
    // vorrebbe dire inchiodare qui la porta del server locale, che è già scritta in Kotlin.
    const response = await get('/device.json', { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { abis?: unknown };
    if (!Array.isArray(body.abis)) return [];
    // L'ordine arriva da `Build.SUPPORTED_ABIS` ed è **per preferenza del sistema**: non si riordina
    // e non si filtra, perché è quell'ordine a decidere la variante.
    return body.abis.filter((abi): abi is string => typeof abi === 'string' && abi !== '');
  } catch {
    return [];
  }
}
